import { useEffect, useState } from "react";
import { cn } from "@/lib/utils/cn";

export type AvatarTone = "you" | "partner";
export type AvatarSize = "xs" | "sm" | "md" | "nav" | "shell" | "lg";

export interface AuthorAvatarProps {
  tone: AvatarTone;
  size?: AvatarSize;
  /** Display label (initials fallback). */
  label?: string;
  /** Image URL (optional). */
  src?: string;
  className?: string;
}

const sizeMap: Record<AvatarSize, { dim: number; text: string }> = {
  xs: { dim: 20, text: "text-[10px]" },
  sm: { dim: 32, text: "text-meta" },
  md: { dim: 40, text: "text-body-sm" },
  // W38.1: the mobile nav's avatar island. 48 inside a 60px island leaves a
  // 6px glass ring per side, which is what makes the left island read as the
  // same size as the menu island (a 40px avatar in the same 60px island left
  // 10px of glass and the eye read the *avatar* as the island).
  nav: { dim: 48, text: "text-body-sm" },
  // W42 F28/F36: the topbar avatar, which is 38px of visual box. `nav` (48) was
  // sized for the retired 60px bottom island, and `AuthorAvatar` sets its box
  // with an inline style, so a width/height class cannot resize it — a new
  // scale entry is the only honest way down. Kept separate from `sm` (32)
  // because 32 inside the 38px `.shell-avatar` left a 3px ring and read as a
  // small avatar in a container rather than as one 38px circle.
  shell: { dim: 38, text: "text-[14px]" },
  lg: { dim: 56, text: "text-h3" },
};

const toneMap: Record<AvatarTone, string> = {
  you: "bg-you-tint text-you-strong border-you/30",
  partner: "bg-partner-tint text-partner-strong border-partner/30",
};

/**
 * P9 — AuthorAvatar.
 *
 * Per `component-api.md §2 P9`:
 *   - xs (20) / sm (32) / md (40) / lg (56)
 *   - tone: 'you' | 'partner' (信头印章 直觉)
 *   - 实色 + hairline border + 12% tint halo
 *
 * **404 fallback (W36.1, profile spec §8 B4).** A `src` that fails to load
 * falls back to the same initial card used when there is no `src` at all,
 * rather than leaving a broken image icon in the card. This is not
 * hypothetical: avatars moved onto the media module in W36.1 and a
 * soft-deleted or purged media row now produces a URL that resolves to 404,
 * and a birthday card that shows a torn-image glyph reads as "your data is
 * broken" instead of "no picture yet".
 *
 * The failed state resets whenever `src` changes, so a re-render with a new
 * URL gets a fresh attempt rather than being stuck on the fallback.
 */
export function AuthorAvatar({ tone, size = "md", label, src, className }: AuthorAvatarProps) {
  const { dim, text } = sizeMap[size];
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  const showImage = Boolean(src) && !failed;

  return (
    <div
      data-component="AuthorAvatar"
      data-tone={tone}
      data-size={size}
      // Drives the B4 test and any future styling: the card is showing an
      // initial *because* the image is absent or failed, which is a different
      // state from "an image is loading".
      data-avatar-state={showImage ? "image" : "initial"}
      className={cn(
        "rounded-full border flex items-center justify-center overflow-hidden shrink-0",
        "font-medium select-none",
        toneMap[tone],
        text,
        className,
      )}
      style={{ width: dim, height: dim }}
      aria-label={label}
    >
      {showImage ? (
        <img
          src={src}
          alt={label ?? ""}
          className="w-full h-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        <span>{(label ?? "").slice(0, 1) || (tone === "you" ? "你" : "對")}</span>
      )}
    </div>
  );
}
